import { Check, Star } from "lucide-react";
import { useLang } from "@/i18n";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { CTAButton, SectionLabel } from "./ui";
import { Reveal } from "./Reveal";
import { cn } from "@/lib/utils";

export const Packages = () => {
  const { t } = useLang();
  const pk = t.packages;
  return (
    <section id="packages" data-testid="packages-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="max-w-2xl mx-auto text-center">
          <SectionLabel>{pk.label}</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-navy-900 leading-tight">
            {pk.title}
          </h2>
          <p className="mt-5 text-lg text-charcoal-700 leading-relaxed">{pk.sub}</p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-3 gap-6 items-stretch">
          {pk.items.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08} className="h-full">
              <div
                data-testid={`package-card-${i}`}
                className={cn(
                  "relative h-full flex flex-col rounded-3xl p-8 border transition-transform duration-300",
                  p.popular
                    ? "brand-gradient-deep text-white border-transparent shadow-[0_30px_70px_-20px_rgba(124,58,237,0.5)] lg:-translate-y-4 lg:scale-[1.03]"
                    : "bg-white border-navy-100 shadow-[0_2px_14px_rgba(6,16,32,0.05)] hover:shadow-xl hover:-translate-y-1"
                )}
              >
                {p.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-gold-500 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-navy-900">
                    <Star size={13} className="fill-navy-900" aria-hidden /> {pk.mostPopular}
                  </span>
                )}

                <h3 className={cn("font-display text-2xl font-semibold", p.popular ? "text-white" : "text-navy-900")}>
                  {p.name}
                </h3>
                <p className={cn("mt-2 text-sm leading-relaxed", p.popular ? "text-white/75" : "text-charcoal-700/80")}>
                  {p.tagline}
                </p>

                <div className="mt-6">
                  <span className={cn("text-xs font-semibold uppercase tracking-wide", p.popular ? "text-gold-400" : "text-gold-600")}>
                    {pk.startingAt}
                  </span>
                  <div className={cn("font-display text-4xl font-bold", p.popular ? "text-white" : "text-navy-900")}>
                    {p.price}
                  </div>
                </div>

                {p.inherits && (
                  <p className={cn("mt-6 text-sm font-semibold", p.popular ? "text-gold-400" : "text-gold-600")}>
                    {p.inherits}
                  </p>
                )}

                <ul className={cn("space-y-3 flex-1", p.inherits ? "mt-5" : "mt-6")}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check size={18} className={cn("mt-0.5 shrink-0", p.popular ? "text-gold-400" : "text-gold-600")} aria-hidden />
                      <span className={cn("text-[15px] leading-snug", p.popular ? "text-white/90" : "text-charcoal-700")}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <CTAButton
                  href="#contact"
                  variant={p.popular ? "primary" : "secondary"}
                  data-testid={`package-cta-${i}`}
                  onClick={() => trackEvent(EVENTS.PACKAGE_CTA, { package: p.name })}
                  className="mt-8 w-full justify-center min-h-[48px]"
                >
                  {p.cta}
                </CTAButton>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10">
          <p className="max-w-3xl mx-auto text-center text-sm text-charcoal-700/80 leading-relaxed" data-testid="packages-disclaimer">
            {pk.disclaimer}
          </p>
          <p className="mt-4 max-w-3xl mx-auto text-center text-sm font-medium text-navy-900/80 leading-relaxed" data-testid="packages-process">
            {pk.process}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
